// DeleteAccountSection — Profile → "מחיקת חשבון" (2026-10-04).
//
// Calls DELETE /user/{uid}/delete-all-data (backend account_deletion.py: strict auth, one DB
// transaction, Strava revoke, Firebase user deletion). Never a single click: open → read the
// warning → type the confirmation word → confirm. The result is shown exactly as the backend
// (2026-10-21: wording aligned with the Privacy Policy §7 — no "permanently deleted" claim, backups disclosed)
// reports it: 'deleted' / 'partial' (data deleted, sign-in account not — retry) / 'failed'
// (nothing deleted).
import React, { useState } from 'react';
import { getAuth } from 'firebase/auth';
import authFetch from '../utils/authFetch';

const CONFIRM_WORD = 'מחק';

// Graphite: a card with a line border; secondary = outline + sub text, primary = lime. Red is kept ONLY for the one
// irreversible action ("מחק לצמיתות", danger) and its warning headline.
const box = {
  borderRadius: 16, border: '1px solid var(--g-line)', padding: '16px',
  background: 'var(--g-card)', fontFamily: "'Heebo',sans-serif", direction: 'rtl',
};
const btn = (filled, disabled, danger = false) => ({
  width: '100%', minHeight: 48, padding: '12px 14px', borderRadius: 999, fontWeight: 700, fontSize: 14,
  fontFamily: "'Heebo',sans-serif", cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.5 : 1,
  background: danger ? '#dc2626' : filled ? 'var(--g-accent)' : 'transparent',
  border: danger ? '1px solid #dc2626' : filled ? '1px solid var(--g-accent)' : '1px solid var(--g-line)',
  color: danger ? '#fff' : filled ? 'var(--g-on-accent)' : 'var(--g-sub)',
});

// Shown whenever the backend reports the Strava revocation failed (result.strava === 'failed'). The data step has
// already removed the Strava token, so a retry cannot revoke it: the manual step is the user's only way.
function StravaManualRemoval() {
  return (
    <p style={{ color: 'var(--g-sub)', fontSize: 14, margin: '0 0 8px' }} data-testid="delete-strava-manual">
      <b>נותר צעד אחד:</b> לא הצלחנו לבטל את ההרשאה של RitmoIQ בחשבון ה-Strava שלך. כדי להסיר אותה, היכנס ב-Strava
      להגדרות ← <bdi dir="ltr">My Apps</bdi> והסר את RitmoIQ.
    </p>
  );
}

export default function DeleteAccountSection({ userId, API_BASE_URL, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // {kind: 'deleted'|'partial'|'failed'|'auth', strava}

  const close = () => { if (!busy) { setOpen(false); setTyped(''); setResult(null); } };

  const runDelete = async () => {
    if (typed.trim() !== CONFIRM_WORD || busy) return;
    setBusy(true);
    setResult(null);
    try {
      // Fresh token for a destructive action (never a stale cached one).
      const user = getAuth().currentUser;
      const token = user ? await user.getIdToken(true) : null;
      if (!token) { setResult({ kind: 'auth' }); return; }
      const res = await authFetch(`${API_BASE_URL}/user/${encodeURIComponent(userId)}/delete-all-data`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      let body = {};
      try { body = await res.json(); } catch { body = {}; }
      if (res.status === 401 || res.status === 403) setResult({ kind: 'auth' });
      else if (res.ok && body.status === 'deleted') setResult({ kind: 'deleted', strava: body.strava });
      else if (body.status === 'partial') setResult({ kind: 'partial', strava: body.strava });
      else setResult({ kind: 'failed', strava: body.strava });
    } catch (err) {
      console.error('delete account failed', err);
      setResult({ kind: 'failed' });
    } finally {
      setBusy(false);
    }
  };

  const finish = () => {
    try { localStorage.clear(); } catch { /* ignore */ }
    try { sessionStorage.clear(); } catch { /* ignore */ }
    if (onDeleted) onDeleted();
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} style={btn(false, false)}>
        מחיקת חשבון
      </button>
    );
  }

  if (result?.kind === 'deleted') {
    const stravaPending = result.strava === 'failed';
    return (
      <div style={box}>
        <p style={{ color: 'var(--g-text)', fontWeight: 700, margin: '0 0 8px' }}>החשבון שלך נמחק.</p>
        <p style={{ color: 'var(--g-sub)', fontSize: 14, margin: '0 0 8px' }} data-testid="delete-success-body">
          נתוני החשבון שלך נמחקו מ-RitmoIQ, וחשבון ההתחברות שלך נמחק.{!stravaPending && ' אין צורך לעשות דבר נוסף.'}
        </p>
        <p style={{ color: 'var(--g-muted)', fontSize: 13, margin: '0 0 8px' }} data-testid="delete-success-backups">
          עותקים בגיבויים של ספקי התשתית שלנו עשויים להישמר לפרק זמן נוסף, בהתאם למחזורי הגיבוי שלהם, כמתואר
          במדיניות הפרטיות (סעיף 7).
        </p>
        {stravaPending && <StravaManualRemoval />}
        <button onClick={finish} style={btn(true, false)}>סיום</button>
      </div>
    );
  }

  const ready = typed.trim() === CONFIRM_WORD;
  return (
    <div style={box}>
      <p style={{ color: '#f87171', fontWeight: 800, margin: '0 0 8px' }}>מחיקת החשבון היא סופית ולא ניתנת לביטול.</p>
      <p style={{ color: 'var(--g-sub)', fontSize: 14, margin: '0 0 6px' }}>יימחקו מ-RitmoIQ:</p>
      <ul style={{ color: 'var(--g-sub)', fontSize: 14, margin: '0 0 10px', paddingInlineStart: 18 }}>
        <li>הפרופיל שלך</li>
        <li>תוכניות האימון</li>
        <li>היסטוריית האימונים והניתוחים</li>
        <li>היסטוריית הצ'אט עם המאמן הדיגיטלי (AI)</li>
        <li>נתונים מחשבונות אימון מחוברים (Garmin דרך Stridee, ו-Strava אם חיברת בעבר); נבקש גם לבטל את החיבורים</li>
        <li>חשבון ההתחברות שלך</li>
      </ul>
      <label style={{ color: 'var(--g-text)', fontSize: 14, display: 'block', margin: '0 0 6px' }}>
        כדי לאשר, הקלד <b>{CONFIRM_WORD}</b>:
      </label>
      <input
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        disabled={busy}
        dir="rtl"
        autoComplete="off"
        style={{
          width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 10, marginBottom: 10,
          background: 'var(--g-bg)', border: '1px solid var(--g-line)', color: 'var(--g-text)', fontSize: 15,
        }}
      />

      {result?.kind === 'partial' && (
        <p style={{ color: 'var(--g-sub)', fontSize: 14, margin: '0 0 10px' }}>
          הנתונים שלך נמחקו, אבל מחיקת חשבון ההתחברות לא הושלמה. לחץ שוב על "מחק לצמיתות" כדי להשלים.
        </p>
      )}
      {result?.kind === 'partial' && result.strava === 'failed' && <StravaManualRemoval />}
      {result?.kind === 'failed' && (
        <p style={{ color: 'var(--g-sub)', fontSize: 14, margin: '0 0 10px' }}>
          המחיקה נכשלה ולא נמחק דבר. נסה שוב מאוחר יותר.
          {(result.strava === 'revoked' || result.strava === 'already_revoked') &&
            ' שים לב: החיבור הקודם ל-Strava בוטל.'}
        </p>
      )}
      {result?.kind === 'auth' && (
        <p style={{ color: 'var(--g-sub)', fontSize: 14, margin: '0 0 10px' }}>
          לא הצלחנו לאמת את זהותך. התנתק, התחבר מחדש ונסה שוב. לא נמחק דבר.
        </p>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={runDelete} disabled={!ready || busy} style={btn(true, !ready || busy, true)}>
          {busy ? 'מוחק…' : 'מחק לצמיתות'}
        </button>
        <button onClick={close} disabled={busy} style={btn(false, busy)}>ביטול</button>
      </div>
    </div>
  );
}
